import { createSelector } from "reselect";
import { generateRunnerId } from "@ppb/betslip-core";
import { ApplicationState } from "@ppb/tbd-store/state/ApplicationState.types";
import { getBettingResolvers } from "../../store/state/sportsbook-betting/betting-resolver";
import {
  getSportsbookBettingReviewLines,
  getSportsbookBettingRunners,
} from "../../store/state/sportsbook-betting/sportsbook-betting-selectors";
import { getBetslipGroup } from "../../store/state/betslip-module/betslip-module-selectors";
import { BetslipApplicationState } from "../../store/state/BetslipApplicationState";
import { buildSelection } from "../connected-sportsbook-betslip-mapper";
import type { BetslipLocalization } from "../../contract";

export type PreviewSelection = {
  id: string;
  title: string;
  subtitle: string;
};

export const createPreviewSelectionsSelector = () => {
  return createSelector(
    [
      (_: ApplicationState, id: string) => id,
      getSportsbookBettingReviewLines,
      getSportsbookBettingRunners,
      (_state: ApplicationState, _id: string, localization: BetslipLocalization) => localization,
      (state: BetslipApplicationState) => getBettingResolvers(getBetslipGroup(state)).getMetadata(state),
    ],
    (id, lines, runners, localization, metadata) => {
      const currentLine = lines[id];
      const runnerIds = currentLine.runners.map((runnerTuple) => generateRunnerId(runnerTuple));

      return runnerIds
        .map((runnerId) => {
          const runner = runners[runnerId];
          const runnerMetadata = metadata[runnerId];

          if (!runner || !runnerMetadata) {
            return null;
          }

          const { title, subtitle } = buildSelection(metadata[runnerId], localization, runner);

          return {
            id: runnerId,
            title,
            subtitle,
          };
        })
        .filter((runnerPreview): runnerPreview is PreviewSelection => !!runnerPreview);
    },
  );
};
